"use client";

import { ChevronDown, Menu, X } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import {
  getGeneralSettingTranslation,
  GENERAL_SETTING_DEFAULTS,
  type NavItem,
  type GeneralSettingSection,
} from "@/app/(dashboard)/dashboard/webmaster/general-setting/general-setting-defaults";
import type { SwitcherLanguage } from "@/components/language-switcher";
import { SiteLanguageSwitcher } from "../ui/SiteLanguageSwitcher";

const HOME_PATH = "/";

export function Header({
  isScrolled,
  data: providedData,
  languages,
}: {
  isScrolled: boolean;
  data?: GeneralSettingSection | null;
  languages: SwitcherLanguage[];
}) {
  const data = providedData ?? GENERAL_SETTING_DEFAULTS;
  const t = useTranslations("site.header");
  const eventT = useTranslations("event");
  const locale = useLocale();
  const generalT = getGeneralSettingTranslation(data, locale);
  const [isOpen, setIsOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState<string | null>(null);
  const pathname = usePathname();
  const normalizedPathname = pathname.replace(/^\/(en|km)(?=\/|$)/, "") || "/";
  const isHomePage = normalizedPathname === HOME_PATH;
  // Khmer is unprefixed ("/"), English lives at "/en".
  const homeHref = locale === "km" ? HOME_PATH : `/${locale}`;
  const resolveHref = (href: string) =>
    href.startsWith("#") && !isHomePage ? `${homeHref}${href}` : href;
  const isNavItemActive = (href: string) => {
    if (href.startsWith("#")) return false;
    return normalizedPathname === href || normalizedPathname.startsWith(`${href}/`);
  };
  const navItems = (() => {
    const eventChildren = [
      { label: locale === "km" ? "វីដេអូសង្ខេបក្រោយព្រឹត្តិការណ៍" : "Post-event recap video", href: "/recap-video" },
      { label: locale === "km" ? "ព្រឹត្តិការណ៍ទាំង3ថ្ងៃ" : "All 3 event days", href: "/event" },
      { label: locale === "km" ? "កម្រងរូបភាព" : "Photo gallery", href: "/event/gallery" },
    ];
    const base: NavItem[] = generalT.header_nav_items.map((item) =>
      item.href === "/event" ? { ...item, children: eventChildren } : item,
    );
    const hasEvent = base.some((item) => item.href === "/event");
    if (hasEvent) return base;
    const eventItem: NavItem = { label: eventT("navLabel"), href: "/event", children: eventChildren };
    const insertAt = base.findIndex((item) => item.href === "/invitation-announcement");
    if (insertAt === -1) return [...base, eventItem];
    return [...base.slice(0, insertAt), eventItem, ...base.slice(insertAt)];
  })();

  return (
    <>
    <header
      className={`sticky top-0 z-[70] transition duration-300 ${
        isScrolled
          ? "border-b border-[#23498a]/10 bg-[rgba(4,17,39,0.86)] backdrop-blur-2xl"
          : "bg-[#071b4a]"
      }`}
    >
      <div className="relative z-[65] flex w-full items-center justify-between gap-3 px-5 py-1.5 sm:px-6 md:py-2 lg:px-8 xl:gap-6 xl:px-10">
        <a href={resolveHref("#top")} className="flex shrink-0 items-center gap-3 text-xl font-black tracking-wide text-white xl:text-2xl">
          MSME-EXPO.COM
        </a>

        <nav className="hidden min-w-0 flex-1 items-center justify-center gap-2.5 lg:flex xl:gap-5 2xl:gap-8">
          {navItems.map((item) =>
            item.children && item.children.length > 0 ? (
              <div
                key={item.href}
                className="relative"
                onMouseEnter={() => setOpenSubmenu(item.href)}
                onMouseLeave={() => setOpenSubmenu((current) => (current === item.href ? null : current))}
              >
                <button
                  type="button"
                  onClick={() => setOpenSubmenu((current) => (current === item.href ? null : item.href))}
                  className={`flex items-center gap-1 whitespace-nowrap text-[clamp(0.85rem,1vw,1.125rem)] font-semibold transition hover:text-white ${
                    isNavItemActive(item.href) ? "text-[#f6cf63]" : "text-white"
                  }`}
                  aria-expanded={openSubmenu === item.href}
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <div
                  className={`absolute left-1/2 top-full z-[80] w-48 -translate-x-1/2 pt-3 transition ${
                    openSubmenu === item.href ? "visible opacity-100" : "invisible opacity-0"
                  }`}
                >
                  <div className="rounded-lg border border-[#23498a]/10 bg-[rgba(4,17,39,0.96)] p-1 shadow-[0_24px_48px_rgba(0,0,0,0.35)] backdrop-blur-2xl">
                    {item.children.map((child) => (
                      <a
                        key={child.href}
                        href={resolveHref(child.href)}
                        onClick={() => setOpenSubmenu(null)}
                        className="block rounded-md px-2 py-1.5 text-sm font-medium text-white/90 transition hover:bg-white/10 hover:text-white"
                      >
                        {child.label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={item.href}
                href={resolveHref(item.href)}
                className={`whitespace-nowrap text-[clamp(0.85rem,1vw,1.125rem)] font-semibold transition hover:text-white ${
                  isNavItemActive(item.href) ? "text-[#f6cf63]" : "text-white"
                }`}
              >
                {item.label}
              </a>
            )
          )}
        </nav>

        <div className="flex shrink-0 items-center gap-2 xl:gap-3">
          <a
            href={data.header_cta_href}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 whitespace-nowrap rounded-full bg-[#f6cf63] px-3 py-1.5 text-sm font-bold text-[#041127] shadow-lg transition hover:bg-[#f0bd3a] lg:inline-flex xl:px-4 xl:py-2"
          >
            {generalT.header_cta_label}
          </a>
          <Link
            href="/login"
            className="hidden items-center gap-2 whitespace-nowrap rounded-full border border-white/20 px-3 py-1.5 text-sm font-semibold text-white transition hover:border-white/40 hover:bg-white/10 lg:inline-flex xl:px-4 xl:py-2"
          >
            {t("login")}
          </Link>
          <SiteLanguageSwitcher languages={languages} className="hidden lg:flex" />
          <button
            type="button"
            onClick={() => setIsOpen((current) => !current)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#23498a]/10 bg-[rgba(10,37,84,0.46)] text-white lg:hidden"
            aria-label={t("menuAria")}
          >
            {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </header>

      <div
        className={`fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm transition-opacity duration-300 ease-out lg:hidden ${
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      <div
        className={`fixed inset-y-0 right-0 z-[60] w-[78%] max-w-[320px] transform border-l border-[#23498a]/10 bg-[rgba(4,17,39,0.96)] shadow-[-24px_0_60px_rgba(0,0,0,0.4)] backdrop-blur-2xl transition-transform duration-300 ease-out lg:hidden ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex h-full flex-col gap-2.5 overflow-y-auto px-5 pb-6 pt-24">
          {navItems.map((item) =>
            item.children && item.children.length > 0 ? (
              <div
                key={item.href}
                className="rounded-lg border border-[#23498a]/10 bg-[rgba(10,37,84,0.46)]"
              >
                <button
                  type="button"
                  onClick={() =>
                    setOpenMobileSubmenu((current) => (current === item.href ? null : item.href))
                  }
                  className="flex w-full items-center justify-between px-4 py-3 text-base font-semibold text-white"
                  aria-expanded={openMobileSubmenu === item.href}
                >
                  {item.label}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${
                      openMobileSubmenu === item.href ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openMobileSubmenu === item.href && (
                  <div className="flex flex-col gap-1 px-2 pb-2">
                    {item.children.map((child) => (
                      <a
                        key={child.href}
                        href={resolveHref(child.href)}
                        onClick={() => {
                          setIsOpen(false);
                          setOpenMobileSubmenu(null);
                        }}
                        className="rounded-md px-2 py-1.5 text-sm font-medium text-white/85"
                      >
                        {child.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <a
                key={item.href}
                href={resolveHref(item.href)}
                onClick={() => setIsOpen(false)}
                className={`rounded-2xl border border-[#23498a]/10 bg-[rgba(10,37,84,0.46)] px-4 py-3 text-base font-semibold ${
                  isNavItemActive(item.href) ? "text-[#f6cf63]" : "text-white"
                }`}
              >
                {item.label}
              </a>
            )
          )}
          <a
            href={data.header_cta_href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setIsOpen(false)}
            className="mt-1 inline-flex items-center justify-center gap-2 rounded-2xl bg-[#f6cf63] px-4 py-3 text-base font-bold text-[#041127]"
          >
            {generalT.header_cta_label}
          </a>
          <Link
            href="/login"
            onClick={() => setIsOpen(false)}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 px-4 py-3 text-base font-semibold text-white"
          >
            {t("login")}
          </Link>
          <SiteLanguageSwitcher languages={languages} className="mt-1 self-start" />
        </div>
      </div>
    </>
  );
}
